import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Logo } from '../shared/Logo';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';
import { LogOut, Lock } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { InstallCTH } from '@/components/shared/InstallCTH';

// Tooltip only when the label is actually truncated — never a redundant
// duplicate of a fully visible label (Founder UAT 2026-06).
const NavLabel = ({ text }) => {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);
  return <span ref={ref} className="truncate" title={truncated ? text : undefined}>{text}</span>;
};

export const Sidebar = ({ nav, portalLabel }) => {
  const { user, logout } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Translate if the label starts with "i18n:", otherwise show as-is
  const tr = (v) => (v && typeof v === 'string' && v.startsWith('i18n:')) ? t(v.slice(5)) : v;

  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden lg:flex w-72 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
      <SidebarInner nav={nav} portalLabel={portalLabel} user={user} tr={tr} t={t} handleLogout={handleLogout} />
    </aside>
  );
};

// CTH V6.0 PWA — same navigation rendered inside a mobile drawer (<lg).
export const MobileSidebar = ({ nav, portalLabel, open, onOpenChange }) => {
  const { user, logout } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  const tr = (v) => (v && typeof v === 'string' && v.startsWith('i18n:')) ? t(v.slice(5)) : v;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[300px] p-0 bg-sidebar text-sidebar-foreground border-sidebar-border flex flex-col"
                    data-testid="mobile-sidebar">
        <SidebarInner nav={nav} portalLabel={portalLabel} user={user} tr={tr} t={t}
                      handleLogout={handleLogout} onNavigate={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
};

const SidebarInner = ({ nav, portalLabel, user, tr, t, handleLogout, onNavigate }) => (
  <>
      <div className="px-5 py-5 border-b border-sidebar-border">
        <Logo variant="light" size="sm" />
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-sidebar-accent px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/80">
          <span className="h-1.5 w-1.5 rounded-full bg-sidebar-active animate-pulse" />
          {tr(portalLabel)}
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
        {nav.map((section, idx) => (
          <div key={idx} className="mb-5">
            {section.label && (
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                {tr(section.label)}
              </div>
            )}
            {section.items.map((item) => {
              if (item.locked) {
                return (
                  <TooltipProvider key={item.to} delayDuration={120}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          data-testid={`nav-locked-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
                          aria-disabled="true"
                          className="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/35 cursor-not-allowed select-none"
                        >
                          <item.icon className="h-4 w-4 shrink-0 text-sidebar-foreground/30" strokeWidth={2} />
                          <NavLabel text={tr(item.label)} />
                          <Lock className="ml-auto h-3.5 w-3.5 text-sidebar-foreground/40" strokeWidth={2.4} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs text-xs leading-snug">
                        {t('sidebar.upgradePrompt', { defaultValue: 'Suite not included in your plan. Contact CTH to upgrade.' })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }
              return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                data-testid={item.featured ? `nav-featured-${item.to.replace(/\//g, '-').replace(/^-/, '')}` : undefined}
                className={({ isActive }) => cn(
                  item.featured
                    ? 'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-smooth ring-1 ring-teal-400/40 bg-gradient-to-r from-teal-500/15 via-teal-400/10 to-transparent shadow-[0_0_24px_-8px_rgba(20,184,166,0.65)] hover:from-teal-500/25 hover:via-teal-400/15'
                    : 'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-smooth',
                  !item.featured && (isActive
                    ? 'bg-sidebar-accent text-white shadow-soft'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-white'),
                  item.featured && (isActive ? 'text-white' : 'text-teal-100'),
                )}
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={cn('h-4 w-4 shrink-0',
                      item.featured ? 'text-teal-300'
                        : (isActive ? 'text-sidebar-active' : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground/80'))} strokeWidth={2} />
                    <NavLabel text={tr(item.label)} />
                    {item.badge && (
                      <span
                        data-testid={`nav-badge-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
                        className={cn(
                        'ml-auto shrink-0 inline-flex items-center rounded-full px-1 py-0 text-[9px] font-semibold uppercase',
                        item.featured
                          ? 'bg-teal-400/25 text-teal-100 ring-1 ring-teal-300/40'
                          : 'bg-sidebar-active/20 text-sidebar-active',
                      )}>
                        {tr(item.badge)}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        {user && (
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <Avatar className="h-9 w-9 border border-sidebar-border">
              <AvatarFallback className="bg-gradient-primary text-white text-xs font-semibold">{user.avatar || (user.name || '').split(' ').map((p)=>p[0]).slice(0,2).join('').toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{user.name}</p>
              <p className="text-xs text-sidebar-foreground/60 truncate">{user.email}</p>
            </div>
            <InstallCTH iconOnly className="h-8 w-8 p-0 text-sidebar-foreground/60 hover:text-white hover:bg-sidebar-accent" />
            <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-sidebar-foreground/60 hover:text-white hover:bg-sidebar-accent">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
  </>
);
